import * as http from '../helpers/http';


export interface Mensagem {
    id: number | string;
    sender: 'user' | 'ai';
    text: string;
    timestamp: string;
}

/**
 * Representa todos os dados da nota técnica colaborativa, incluindo o array armazenado na coluna `conversa_ia`.
 */
export interface DataAcaoPreparatoria {
    proposicao: string;
    id_proposicao: number;
    prazo: string;
    responsaveis: string;
    id_acao_preparatoria: number;
    conversa_ia: { conversa_ia: Mensagem[] } | null;
    summary_ia: string | null;
}

/** Resultado da busca por notas técnicas anteriores (sem custo de IA). */
export interface INotaSimilar {
    id: number;
    id_acao_preparatoria: number;
    proposicao: string | null;
    criado_em: string | null;
    trecho: string;
}

/** Nota técnica anexada ao contexto da conversa. */
export interface INotaContexto {
    id: number;
    id_acao_preparatoria: number;
    proposicao: string | null;
    criado_em: string | null;
}

class ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService {

    Get = async (id_acao_preparatoria: number): Promise<any> => {
        const result = await http.get(`/proposicao/acao-preparatoria/${id_acao_preparatoria}/nota-tecnica-colaborativa`);
        if (result.success) {
            return result.data.data
        }
        return undefined;
    }
    Update = async (data: any, id_acao_preparatoria: number,): Promise<boolean> => {
        const result = await http.put(`/proposicao/acao-preparatoria/${id_acao_preparatoria}/nota-tecnica-colaborativa`, data);
        if (result.success) { return result.success }
        return false;
    };

    UpdateConversas = async (
        id_acao_preparatoria: number,
        finalArray: Mensagem[],
        summaryText: string
    ): Promise<boolean> => {
        // Monta um payload que contém tanto o array de mensagens quanto o texto do summary
        const payload = {
            conversa_ia: finalArray,
            summary_ia: summaryText
        };
        // Reapontamos o mesmo endpoint /conversa, agora esperando também summary_ia
        const result = await http.put(
            `/proposicao/acao-preparatoria/${id_acao_preparatoria}/nota-tecnica-colaborativa/conversa`,
            payload
        );
        return result.success;
    };

    Delete = async (id_acao_preparatoria: number): Promise<boolean> => {
        const result = await http.del(`/proposicao/acao-preparatoria/${id_acao_preparatoria}/nota-tecnica-colaborativa`);
        if (result.success) { return true }
        return false;
    };

    PostComments = async (id_nota_tecnica_colaborativa: number, data: any): Promise<number> => {
        const result = await http.post(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_nota_tecnica_colaborativa}/comment`, data);
        if (result.success) { return result.data[0] }
        return 0;
    };

    DeleteComments = async (id_nota_tecnica_colaborativa: number): Promise<boolean> => {
        const result = await http.del(`/proposicao/acao-preparatoria/${id_nota_tecnica_colaborativa}/nota-tecnica-colaborativa`);
        if (result.success) { return true }
        return false;
    };

    PostNewUserEditor = async (id_nota_tecnica_colaborativa: number, id_current_user: number): Promise<any> => {
        const result = await http.put(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_nota_tecnica_colaborativa}/user-editing`, id_current_user);
        if (result.success) { return result }
        return false;
    };

    PostSubComments = async (id_nota_colab: number, data: any): Promise<number> => {
        const result = await http.post(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/comment/${id_nota_colab}`, data);
        if (result.success) { return result.data[0] }
        return 0;
    };

    GetComments = async (id_nota_tecnica_colaborativa: number): Promise<any> => {
        const result = await http.get(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_nota_tecnica_colaborativa}/comments`);
        if (result.success) {
            return result.data
        }
        return undefined;
    }

    GetUserEditing = async (id_nota_tecnica_colaborativa: number): Promise<any> => {
        const result = await http.get(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_nota_tecnica_colaborativa}/user-editing`);
        if (result.success) {
            return result.data
        }
        return undefined;
    }

    GetSubComments = async (): Promise<any> => {
        const result = await http.get(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/comment`);

        if (result.success) {
            return result.data
        }
        return undefined;
    }

    DeleteSubComment = async (id: number): Promise<boolean> => {
        const result = await http.put(`/proposicao/acao-preparatoria/nota-tecnica-colaborativa/comment/sub-comment/${id}`, null);
        if (result.success) { return result.success }
        return false;
    };

    openAiInteraction = async (
        id_nota_tecnica_colaborativa: number,
        data: any
    ): Promise<any> => {
        const result = await http.post(
            `/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_nota_tecnica_colaborativa}/interaction-ai`,
            data
        );
        if (result.success) {
            return result.data;
        }
        // A API explica o motivo em { message } — IA desligada, sem crédito, etc.
        // Devolver 0 fazia a tela cair no genérico "Erro: sem resposta".
        throw new Error((result as any)?.data?.message || 'Não foi possível obter resposta da IA.');
    };

    // ---------------------------------------------------------------------
    // Notas técnicas similares e contexto da conversa.
    // Nenhuma destas chamadas consome IA: busca e listagem são consultas ao
    // banco; anexar apenas registra a referência. O custo só existe depois,
    // quando a conversa usar a nota anexada.
    // ---------------------------------------------------------------------

    BuscarNotasSimilares = async (
        id_acao_preparatoria: number,
        termo: string
    ): Promise<{ resultados: INotaSimilar[]; max_notas: number }> => {
        const result = await http.get(
            `/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_acao_preparatoria}/similares?termo=${encodeURIComponent(termo)}`
        );
        if (result.success) return result.data;
        throw new Error((result as any)?.data?.message || 'Não foi possível buscar notas similares.');
    };

    GetContextoNotas = async (
        id_acao_preparatoria: number
    ): Promise<{ notas: INotaContexto[]; max_notas: number }> => {
        const result = await http.get(
            `/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_acao_preparatoria}/contexto-notas`
        );
        if (result.success) return result.data;
        return { notas: [], max_notas: 3 };
    };

    AnexarNotaContexto = async (
        id_acao_preparatoria: number,
        id_nota: number
    ): Promise<{ notas: INotaContexto[]; max_notas: number }> => {
        const result = await http.post(
            `/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_acao_preparatoria}/contexto-notas`,
            { id_nota }
        );
        if (result.success) return result.data;
        throw new Error((result as any)?.data?.message || 'Não foi possível anexar a nota.');
    };

    DesanexarNotaContexto = async (
        id_acao_preparatoria: number,
        id_nota: number
    ): Promise<{ notas: INotaContexto[]; max_notas: number }> => {
        const result = await http.del(
            `/proposicao/acao-preparatoria/nota-tecnica-colaborativa/${id_acao_preparatoria}/contexto-notas/${id_nota}`
        );
        if (result.success) return result.data;
        throw new Error((result as any)?.data?.message || 'Não foi possível remover a nota.');
    };


}
export default new ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService();
